// ============================================
// Event Socket Types
// ============================================

export type StreamerRole = "organiser" | "challenger";
export type UserRole = "organiser" | "challenger" | "audience";
export type StreamStatus = "pending" | "live" | "paused" | "completed";

// ============================================
// Client → Server Payloads
// ============================================

export interface JoinEventPayload {
  eventId: string;
  userId: string;
  role: UserRole;
}

export interface StreamStartPayload {
  username: string;
  role: StreamerRole;
}

export interface StreamDisconnectPayload {
  username: string;
  role: StreamerRole;
  reason?: string;
}

export interface StreamReconnectPayload {
  username: string;
  role: StreamerRole;
}

export interface StreamStopPayload {
  username: string;
  role: StreamerRole;
}

// ============================================
// Server → Client Broadcast Payloads
// ============================================

export interface StreamEventBroadcast {
  type: string;
  eventId: string;
  username: string;
  role: StreamerRole;
  message: string;
  streamLive: boolean;
  reason?: string;
  timestamp: string;
}

export interface ResultBroadcast {
  type: "success" | "dispute";
  eventId: string;
  winner?: string;
  loser?: string;
  disputeId?: string;
  disputeEndTime?: string;
  reason?: string;
  message: string;
  timestamp: string;
  giftValues?: {
    user1Gifts: number;
    user2Gifts: number;
    sharedGifts: number;
  };
  winnerDetails?: {
    username: string;
    totalGiftValue: number;
    role: string;
  };
}

export interface VotingEnabledBroadcast {
  type: "public_voting_enabled";
  eventId: string;
  publicVotingEnabled: boolean;
  message: string;
  timestamp: string;
}

export interface VotingCountBroadcast {
  type: "public_voting_count";
  eventId: string;
  voteCount: {
    organiser: number;
    challenger: number;
  };
  timestamp: string;
}

// ============================================
// Socket Event Names
// ============================================

export const SOCKET_EVENTS = {
  // Client → Server
  JOIN: "join",
  LEAVE: "leave",
  STREAM_START: "stream:start",
  STREAM_DISCONNECT: "stream:disconnect",
  STREAM_RECONNECT: "stream:reconnect",
  STREAM_STOP: "stream:stop",

  // Server → Client
  STREAM_STARTED: "stream:started",
  STREAM_DISCONNECTED: "stream:disconnected",
  STREAM_RECONNECTED: "stream:reconnected",
  STREAM_STOPPED: "stream:stopped",
  RESULT: "result",
  VOTING_ENABLED: "voting:enabled",
  VOTING_COUNT: "voting:count",
} as const;

// ============================================
// Context State Types
// ============================================

export interface EventSocketState {
  isConnected: boolean;
  connectionError: string | null;
  eventId: string | null;

  // Stream status
  organiserStatus: StreamStatus;
  challengerStatus: StreamStatus;

  // Result
  result: ResultBroadcast | null;

  // Voting
  votingEnabled: boolean;
  voteCount: { organiser: number; challenger: number } | null;
}

export const initialEventSocketState: EventSocketState = {
  isConnected: false,
  connectionError: null,
  eventId: null,
  organiserStatus: "pending",
  challengerStatus: "pending",
  result: null,
  votingEnabled: false,
  voteCount: null,
};
